import { useMemo, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { DocumentTextIcon, TrashIcon } from "@heroicons/react/24/outline";
import { createTable, RenderingPresetType, TableHeaderSpec } from "../../table/CreateTable.tsx";
import { ForeignAmountLabel } from "../../general/ForeignAmountLabel.tsx";
import Popup from "../../general/Popup.tsx";
import { PrimaryButton } from "../../general/PrimaryButton.tsx";
import { cleanDocumentFileName } from "../../../lib/TFICLib";
import { DocumentTagEnum } from "../../../types/enums";
import TableTags from "../../../pages/app/partner/pages/PartnerClientDocuments/components/TableTags.tsx";
import type { Money } from "../../../lib/Money.ts";
import {
    BillDuplicate,
    BillDuplicateGroup,
    BOOKED_DUPLICATE_CANNOT_BE_DELETED,
    DuplicateDeleteAction,
    getDuplicateDeleteAction,
} from "./possibleDuplicates";

interface PossibleDuplicatesModalProps {
    open: boolean;
    onClose: () => void;
    group: BillDuplicateGroup | null;
    isLoading: boolean;
    isError: boolean;
    onView: (bill: BillDuplicate) => void;
    onDelete: (bill: BillDuplicate) => Promise<void>;
}

type BillDuplicateRow = BillDuplicate & { id: string };

const DELETE_LABELS: Record<DuplicateDeleteAction, string> = {
    [DuplicateDeleteAction.DELETE]: "Verwijderen",
    [DuplicateDeleteAction.UNMATCH_AND_DELETE]: "Ontkoppelen en verwijderen",
    [DuplicateDeleteAction.BLOCKED]: "Verwijderen",
};

export default function PossibleDuplicatesModal({
    open,
    onClose,
    group,
    isLoading,
    isError,
    onView,
    onDelete,
}: PossibleDuplicatesModalProps) {
    const [billToDelete, setBillToDelete] = useState<BillDuplicate | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteConfirm = async () => {
        if (!billToDelete) return;
        setIsDeleting(true);
        try {
            await onDelete(billToDelete);
        } finally {
            setIsDeleting(false);
            setBillToDelete(null);
        }
    };

    const spec: TableHeaderSpec<BillDuplicateRow> = useMemo(() => ({
        columns: [
            {
                field: "file_name",
                name: "Bestand",
                style: { widthMultiplier: 2.5 },
                customRender: (bill) => (
                    <div className="flex items-center">
                        <div className="mr-3 flex-shrink-0 flex items-center justify-center">
                            <DocumentTextIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                        </div>
                        <span className="block max-w-36 truncate text-sm text-gray-900" title={cleanDocumentFileName(bill.file_name)}>{cleanDocumentFileName(bill.file_name)}</span>
                    </div>
                ),
            },
            {
                field: "relation_name",
                name: "Relatie",
                style: { widthMultiplier: 1.75 },
                customRender: (bill) => <span className="block max-w-28 truncate" title={bill.relation_name}>{bill.relation_name}</span>,
            },
            { field: "invoice_number", name: "Factuurnummer", style: { nowrap: true, widthMultiplier: 0.9 } },
            { field: "invoice_date", name: "Factuurdatum", renderingPreset: RenderingPresetType.Date, style: { widthMultiplier: 0.8 } },
            { field: "created_at", name: "Geüpload op", renderingPreset: RenderingPresetType.Date, style: { widthMultiplier: 0.8 } },
            {
                field: "total_amount_payable",
                name: "Bedrag",
                customRender: (bill) => (
                    <ForeignAmountLabel
                        amount={{signed_amount: bill.total_amount_payable, currency: bill.currency} as Money}
                        origin={bill.monetary_origin ?? null}
                        colorAid={true}
                    />
                ),
                style: { align: "right", nowrap: true, widthMultiplier: 0.8 },
            },
            {
                field: "__tags__",
                name: "Tags",
                style: { widthMultiplier: 0.7 },
                customRender: (bill) => <TableTags tags={bill.is_matched ? [DocumentTagEnum.MATCHED] : []} />,
            },
            {
                field: "__actions__",
                name: "",
                style: { widthMultiplier: 0.3 },
                customRender: (bill) => {
                    const action = getDuplicateDeleteAction(bill);
                    const isBlocked = action === DuplicateDeleteAction.BLOCKED;
                    return (
                        <div className="flex justify-end" onClick={(event) => event.stopPropagation()}>
                            <button
                                type="button"
                                aria-label={DELETE_LABELS[action]}
                                title={isBlocked ? BOOKED_DUPLICATE_CANNOT_BE_DELETED : DELETE_LABELS[action]}
                                disabled={isBlocked}
                                onClick={() => setBillToDelete(bill)}
                                className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                        </div>
                    );
                },
            },
        ],
        onRowClick: onView,
        options: { allowFullRowClick: true },
    }), [onView]);

    const rows: BillDuplicateRow[] = (group?.bills ?? []).map((bill) => ({...bill, id: bill.bill_id}));
    const Table = useMemo(() => createTable<BillDuplicateRow>([], spec), [spec]);
    const confirmAction = billToDelete ? getDuplicateDeleteAction(billToDelete) : DuplicateDeleteAction.DELETE;

    return (
        <>
            <Dialog open={open} onClose={onClose} className="relative z-40">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-black/30 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <DialogPanel
                        transition
                        className="w-full max-w-5xl rounded-lg bg-white p-6 shadow-xl transition-all data-[closed]:opacity-0 data-[closed]:scale-95 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                    >
                        <div className="flex items-center justify-between mb-1">
                            <DialogTitle as="h3" className="text-xl font-semibold text-gray-900">
                                Mogelijke duplicaten
                            </DialogTitle>
                            <button
                                type="button"
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                onClick={onClose}
                                aria-label="Sluiten"
                            >
                                <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        {group && (
                            <p className="mb-4 text-sm text-gray-600">
                                Deze documenten hebben dezelfde relatie ({group.relation_name}) en hetzelfde factuurnummer ({group.invoice_number}).
                            </p>
                        )}

                        <div className="max-h-[60vh] overflow-auto">
                            {isLoading && <div className="text-sm text-gray-500">Laden...</div>}
                            {isError && !isLoading && (
                                <div className="text-sm text-red-700">
                                    Er zijn geen mogelijke duplicaten (meer) voor dit document.
                                </div>
                            )}
                            {!isLoading && !isError && <Table data={rows} />}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <PrimaryButton title="Sluiten" onClick={onClose} />
                        </div>
                    </DialogPanel>
                </div>
            </Dialog>
            <Popup
                open={billToDelete}
                onClose={() => setBillToDelete(null)}
                onConfirm={handleDeleteConfirm}
                title={DELETE_LABELS[confirmAction]}
                variant="danger"
                confirmButtonDisplayLabel={DELETE_LABELS[confirmAction]}
                confirmButtonDisabled={isDeleting}
            >
                <p className="text-sm text-gray-700">
                    {confirmAction === DuplicateDeleteAction.UNMATCH_AND_DELETE
                        ? "Dit document is gekoppeld aan een betaling. De koppeling wordt verwijderd en daarna wordt het document verwijderd."
                        : "Weet je zeker dat je dit document wilt verwijderen?"}
                </p>
            </Popup>
        </>
    );
}
